import {
	ReactElement,
	useContext,
	useMemo,
	useState
} from "react";
import { DestinationWindow } from "client/Api/Apps/common";
import { NedapOnsLiveInformationProvider } from "./provider";
import { LiveTileContent } from "../LiveTileContent";
import { never } from "client_legacy/ndaw/errors";
import { NedapOnsConnectionIdentifierViewModel } from "client/Api/Apps/EditApp/saveApp";
import { TileTabProps } from "../TileTabSelector";
import { NedapOnsResourceKind } from "client/Api/Workspace/LiveInformation/NedapOns/nedapOnsLiveInformation";
import { Guid } from "client_legacy/ndaw/guid";
import { NedapOnsMySchedule } from "./NedapOnsMySchedule";
import { NedapOnsMyPlanning } from "./NedapOnsMyPlanning";
import { NedapOnsClients } from "./NedapOnsClients";
import { TileContext } from "../TileContext";
import { useUserTimezone } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { NedapOnsTabDisplayModel } from "client/Api/Workspace/workspace";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type NedapOnsLiveTileContentProps = {
	connection: NedapOnsConnectionIdentifierViewModel;
	destinationWindow: DestinationWindow;
	provider: NedapOnsLiveInformationProvider;
	tabs: readonly NedapOnsTabDisplayModel[];
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const NedapOnsLiveTileContent = (props: NedapOnsLiveTileContentProps): ReactElement => {
	const {
		connection,
		destinationWindow,
		provider,
		tabs,
		onOpenTileMenu
	} = props;

	const connectionId: number = connection.id;

	const {
		dimension,
		icon,
		id,
		name,
		environmentAppId,
		interactionState,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const userTimezone: TimeZone = useUserTimezone();

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const [activeTabId, setActiveTabId] = useState<Guid | undefined>(tabs[0]?.uniqueId);
	const tabProps = useMemo(
		(): readonly TileTabProps[] => {
			return tabs.map(x => {
				const result: TileTabProps = {
					uniqueId: x.uniqueId,
					name: x.name
				};

				return result;
			});
		},
		[tabs]
	);

	const activeTab: NedapOnsTabDisplayModel | undefined = tabs.find(x => x.uniqueId === activeTabId);

	const renderActiveTab = (): ReactElement | null => {
		if (!activeTab) {
			return null;
		}

		switch (activeTab.resourceKind) {
			case NedapOnsResourceKind.MySchedule:
				return (
					<NedapOnsMySchedule
						tabId={activeTab.uniqueId}
						connectionId={connectionId}
						provider={provider}
						dimensionToRender={dimension}
						destinationWindow={destinationWindow}
						environmentAppId={environmentAppId}
						timezone={userTimezone}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				);
			case NedapOnsResourceKind.Clients:
				return (
					<NedapOnsClients
						connectionId={connectionId}
						dimensionToRender={dimension}
						destinationWindow={destinationWindow}
						environmentAppId={environmentAppId}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				);
			case NedapOnsResourceKind.MyPlanning:
				return (
					<NedapOnsMyPlanning
						tabId={activeTab.uniqueId}
						connectionId={connectionId}
						provider={provider}
						dimensionToRender={dimension}
						destinationWindow={destinationWindow}
						environmentAppId={environmentAppId}
						timezone={userTimezone}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				);
			default:
				never(activeTab.resourceKind);
		}
	};

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header icon={icon}>
				<LiveTileContent.TabSelector
					value={activeTabId}
					onChange={setActiveTabId}
					tabs={tabProps}
					tileId={id}
					tileName={name}
				/>
			</LiveTileContent.Header>
			{renderActiveTab()}
		</LiveTileContent>
	);
};
NedapOnsLiveTileContent.displayName = "NedapOnsLiveTileContent";
